/**
 * Hilfsfunktionen für die Verarbeitung von Öffnungszeiten
 * Unterstützt verschiedene API-Formate (Drizzle/SQL)
 * 
 * WICHTIG: Unterstützt beide Wochentag-Formate:
 * - JS-Format: 0=Sonntag, 1-6=Mon-Sat (wie JavaScript Date.getDay())
 * - ISO-Format: 1-7=Mon-Sun (wie ISO 8601)
 */

// Importiere den Typ aus der gemeinsamen types.ts-Datei
import { OpeningHour } from '@/lib/types';

/**
 * Erkennt welches Wochentag-Format die Daten verwenden
 */
function detectWeekdayFormat(openingHours: OpeningHour[]): 'js' | 'iso' {
  for (const hour of openingHours) {
    const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
    if (dayValue === 0) return 'js';  // 0 existiert nur in JS-Format
    if (dayValue === 7) return 'iso'; // 7 existiert nur in ISO-Format
  }
  return 'js'; // Default: JS-Format (Speiseguru DB Schema)
}

/**
 * Normalisiere Zeitwerte: Entferne Sekunden (HH:MM:SS -> HH:MM)
 */
function normalizeTime(time: string | null | undefined): string {
  if (!time) return '';
  const timeStr = String(time);
  return timeStr.split(':').slice(0, 2).join(':');
}

/**
 * Prüft, ob ein Restaurant aktuell geöffnet ist.
 * @param openingHours Array von Öffnungszeiten
 * @returns true, wenn das Restaurant aktuell geöffnet ist
 */
export function isOpen(openingHours: OpeningHour[] | null | undefined): boolean {
  if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
    return false;
  }

  const now = new Date();
  const jsDay = now.getDay(); // 0=Sonntag, 1-6=Mon-Sat
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeStr = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;

  const format = detectWeekdayFormat(openingHours);
  const currentDay = format === 'js' ? jsDay : (jsDay === 0 ? 7 : jsDay);

  for (const hour of openingHours) {
    const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
    const openTime = normalizeTime(hour.openStart || hour.opening || '');
    const closeTime = normalizeTime(hour.openEnd || hour.closing || '');
    
    if (dayValue === currentDay && openTime && closeTime) {
      // Handle over-midnight times
      if (closeTime < openTime) {
        // Over midnight: check if in first part (before midnight) or second part (after midnight)
        if (currentTimeStr >= openTime || currentTimeStr < closeTime) {
          return true;
        }
      } else {
        if (currentTimeStr >= openTime && currentTimeStr < closeTime) {
          return true;
        }
      }
    }
  }
  
  // Also check yesterday for over-midnight times
  const yesterdayJsDay = jsDay === 0 ? 6 : jsDay - 1;
  const yesterdayDay = format === 'js' ? yesterdayJsDay : (yesterdayJsDay === 0 ? 7 : yesterdayJsDay);
  
  for (const hour of openingHours) {
    const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
    const openTime = normalizeTime(hour.openStart || hour.opening || '');
    const closeTime = normalizeTime(hour.openEnd || hour.closing || '');
    
    if (dayValue === yesterdayDay && openTime && closeTime && closeTime < openTime) {
      // Over-midnight from yesterday
      if (currentTimeStr < closeTime) {
        return true;
      }
    }
  }
  
  return false;
}

/**
 * Gibt formatierte Informationen zur nächsten Öffnungszeit zurück
 */
export function getNextOpeningTime(openingHours: OpeningHour[] | null | undefined): string {
  if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
    return "Öffnungszeiten nicht verfügbar";
  }

  const now = new Date();
  const jsDay = now.getDay(); // 0=Sonntag, 1-6=Mon-Sat
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeStr = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
  
  const format = detectWeekdayFormat(openingHours);
  const currentDay = format === 'js' ? jsDay : (jsDay === 0 ? 7 : jsDay);
  
  const weekdaysJs = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  const weekdaysIso = ['', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
  
  // Heute noch öffnen?
  const todayHours = openingHours.filter(hour => {
    const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
    return dayValue === currentDay;
  }).sort((a, b) => {
    const aTime = normalizeTime(a.openStart || a.opening || '');
    const bTime = normalizeTime(b.openStart || b.opening || '');
    return aTime.localeCompare(bTime);
  });
  
  for (const hour of todayHours) {
    const openTime = normalizeTime(hour.openStart || hour.opening || '');
    if (openTime && currentTimeStr < openTime) {
      return `Heute, ${openTime} Uhr`;
    }
  }
  
  // In den nächsten Tagen öffnen?
  for (let i = 1; i <= 7; i++) {
    let nextDay: number;
    if (format === 'js') {
      nextDay = (jsDay + i) % 7;
    } else {
      nextDay = ((currentDay - 1 + i) % 7) + 1;
    }
    
    const nextDayHours = openingHours.filter(hour => {
      const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
      return dayValue === nextDay;
    }).sort((a, b) => {
      const aTime = normalizeTime(a.openStart || a.opening || '');
      const bTime = normalizeTime(b.openStart || b.opening || '');
      return aTime.localeCompare(bTime);
    });
    
    if (nextDayHours.length > 0) {
      const firstOpening = normalizeTime(nextDayHours[0].openStart || nextDayHours[0].opening || '');
      if (firstOpening) {
        if (i === 1) {
          return `Morgen, ${firstOpening} Uhr`;
        }
        const weekdayName = format === 'js' ? weekdaysJs[nextDay] : weekdaysIso[nextDay];
        return `${weekdayName}, ${firstOpening} Uhr`;
      }
    }
  }
  
  return "Öffnungszeiten nicht verfügbar";
}

/**
 * Formatiert Öffnungszeiten für die Anzeige
 */
export function formatOpeningHours(openingHours: OpeningHour[] | null | undefined): string[] {
  const weekdays = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
  const result: string[] = [];
  
  if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
    weekdays.forEach(weekday => result.push(`${weekday}: Geschlossen`));
    return result;
  }
  
  const format = detectWeekdayFormat(openingHours);
  
  // Gruppiere Öffnungszeiten nach Wochentag
  const hoursByDay: Record<number, OpeningHour[]> = {};
  
  openingHours.forEach(hour => {
    const day = hour.weekday !== undefined ? hour.weekday : (hour.day || 0);
    // Convert to 1-7 for grouping (1=Mon, 7=Sun)
    let normalizedDay: number;
    if (format === 'js') {
      normalizedDay = day === 0 ? 7 : day; // Sunday: 0 -> 7
    } else {
      normalizedDay = day;
    }
    
    if (normalizedDay >= 1 && normalizedDay <= 7) {
      if (!hoursByDay[normalizedDay]) {
        hoursByDay[normalizedDay] = [];
      }
      hoursByDay[normalizedDay].push(hour);
    }
  });
  
  // Formatiere Öffnungszeiten für jeden Tag
  for (let i = 1; i <= 7; i++) {
    const dayHours = hoursByDay[i] || [];
    const weekday = weekdays[i - 1];
    
    if (dayHours.length === 0) {
      result.push(`${weekday}: Geschlossen`);
      continue;
    }
    
    dayHours.sort((a, b) => {
      const aTime = a.openStart || a.opening || '';
      const bTime = b.openStart || b.opening || '';
      return aTime.localeCompare(bTime);
    });
    
    const timeRanges = dayHours.map(hour => {
      const start = normalizeTime(hour.openStart || hour.opening || '');
      const end = normalizeTime(hour.openEnd || hour.closing || '');
      
      if (hour.pauseStart && hour.pauseEnd) {
        const pauseStart = normalizeTime(hour.pauseStart);
        const pauseEnd = normalizeTime(hour.pauseEnd);
        return `${start} - ${pauseStart} Uhr, ${pauseEnd} - ${end} Uhr`;
      }
      
      return `${start} - ${end} Uhr`;
    });
    
    result.push(`${weekday}: ${timeRanges.join(', ')}`);
  }
  
  return result;
}

/**
 * Prüft, ob ein Restaurant basierend auf Lieferzeiten gerade liefert
 */
export function isDelivering(deliveryHours: OpeningHour[] | null | undefined): boolean {
  return isOpen(deliveryHours);
}

/**
 * Ermittelt den aktuellen Schließungszeitpunkt heute
 */
export function getCurrentClosingTime(openingHours: OpeningHour[] | null | undefined): string | null {
  if (!openingHours || !Array.isArray(openingHours) || openingHours.length === 0) {
    return null;
  }

  const now = new Date();
  const jsDay = now.getDay();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeStr = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;

  const format = detectWeekdayFormat(openingHours);
  const currentDay = format === 'js' ? jsDay : (jsDay === 0 ? 7 : jsDay);

  for (const hour of openingHours) {
    const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
    const openTime = normalizeTime(hour.openStart || hour.opening || '');
    const closeTime = normalizeTime(hour.openEnd || hour.closing || '');
    
    if (dayValue === currentDay && openTime && closeTime) {
      // Handle over-midnight
      if (closeTime < openTime) {
        if (currentTimeStr >= openTime || currentTimeStr < closeTime) {
          return closeTime;
        }
      } else {
        if (currentTimeStr >= openTime && currentTimeStr < closeTime) {
          return closeTime;
        }
      }
    }
  }
  
  // Check yesterday for over-midnight
  const yesterdayJsDay = jsDay === 0 ? 6 : jsDay - 1;
  const yesterdayDay = format === 'js' ? yesterdayJsDay : (yesterdayJsDay === 0 ? 7 : yesterdayJsDay);
  
  for (const hour of openingHours) {
    const dayValue = hour.weekday !== undefined ? hour.weekday : hour.day;
    const openTime = normalizeTime(hour.openStart || hour.opening || '');
    const closeTime = normalizeTime(hour.openEnd || hour.closing || '');
    
    if (dayValue === yesterdayDay && openTime && closeTime && closeTime < openTime) {
      if (currentTimeStr < closeTime) {
        return closeTime;
      }
    }
  }

  return null;
}

/**
 * Verbesserte Version der Funktion
 */
export function isRestaurantOpenNow(openingHours: OpeningHour[]) {
  const isCurrentlyOpen = isOpen(openingHours);
  const nextOpening = isCurrentlyOpen ? null : getNextOpeningTime(openingHours);
  const currentClosingTime = isCurrentlyOpen ? getCurrentClosingTime(openingHours) : null;
  
  return {
    isOpen: isCurrentlyOpen,
    currentStatus: isCurrentlyOpen ? 'geöffnet' : 'geschlossen',
    opensToday: false,
    nextOpeningTime: nextOpening,
    nextClosingTime: currentClosingTime || '22:00'
  };
}

/**
 * Alte API-Version für Abwärtskompatibilität
 */
export function isRestaurantDeliveringNow(deliveryHours: OpeningHour[] | null | undefined) {
  const isCurrentlyDelivering = isDelivering(deliveryHours);
  const nextDeliveryTime = isCurrentlyDelivering ? null : getNextOpeningTime(deliveryHours);
  
  return {
    isOpen: isCurrentlyDelivering,
    currentStatus: isCurrentlyDelivering ? 'liefert' : 'liefert nicht',
    opensToday: false,
    nextOpeningTime: nextDeliveryTime,
    nextClosingTime: '22:00'
  };
}